<template>
  <div class="sub-accounts-control-bar">
    <div class="control-bar-content">
      <b-popover target="dropdown-trigger" triggers="" :show.sync="showAccountSelectionWarning" placement="top">
        <i class="ion ion-information-circle-outline"></i>
        {{ accountSelectionWarning }}
      </b-popover>
      <div class="accounts-section">
        <div class="selected-chips-inline">
          <div
            v-for="account in selectedAccountsData"
            :key="account.id"
            class="account-chip"
          >
            <span
              class="chip-color-dot"
              :style="{ backgroundColor: account.color }"
            ></span>
            <span class="chip-text">{{ account.name }}</span>
            <button @click="removeAccount(account.id)" class="chip-remove">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="dropdown-container" ref="accountsDropdown">
          <button
            id="dropdown-trigger"
            class="dropdown-trigger"
            @click.stop="toggleAccountsDropdown"
            :class="{ 'is-open': isAccountsDropdownOpen }"
            :aria-expanded="isAccountsDropdownOpen"
            aria-haspopup="listbox"
            aria-label="Account selector dropdown"
          >
            <span class="dropdown-label">ACCOUNTS</span>
            <span
              v-if="localSelectedAccountIds.length === 0"
              class="dropdown-value"
            >Select accounts</span>
            <i class="fas fa-chevron-down dropdown-arrow"></i>
          </button>

          <div v-if="isAccountsDropdownOpen" class="dropdown-menu">
            <div class="dropdown-header">
              <input
                type="text"
                v-model="accountSearchQuery"
                placeholder="Search accounts..."
                class="search-input"
                @click.stop
                aria-label="Search accounts"
              />
              <div class="quick-actions">
                <button @click.stop="selectAllAccounts" class="quick-action-btn">All</button>
                <button @click.stop="deselectAllAccounts" class="quick-action-btn">Clear</button>
              </div>
            </div>

            <div class="accounts-list">
              <div
                v-if="filteredAccounts.length === 0 && accountSearchQuery"
                class="no-results"
              >
                No accounts found
              </div>
              <div
                v-for="account in filteredAccounts"
                :key="account.id"
                class="account-item"
                @click.stop="toggleAccount(account)"
              >
                <input
                  type="checkbox"
                  :checked="isAccountSelected(account.id)"
                  @change="toggleAccount(account)"
                  class="account-checkbox"
                  @click.stop
                />
                <span
                  class="account-color-dot"
                  :style="{ backgroundColor: account.color }"
                ></span>
                <span class="account-name">{{ account.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="vertical-separator"></div>

      <div class="date-section">
        <span class="section-label">DATE RANGE</span>
        <div class="date-pickers-inline">
          <input
            type="date"
            v-model="localStartDate"
            class="date-input"
            :max="localEndDate || null"
            @change="onStartDateChange"
          />
          <span class="date-separator">—</span>
          <input
            type="date"
            v-model="localEndDate"
            class="date-input"
            :min="localStartDate || null"
            @change="onEndDateChange"
          />
        </div>
      </div>

      <div class="vertical-separator"></div>

      <div class="period-section">
        <button
          v-for="period in periodOptions"
          :key="period.key"
          class="period-btn"
          :class="{ 'is-active': selectedPeriod === period.key }"
          @click="selectPeriod(period)"
          :aria-label="`Select ${period.label} period`"
          :aria-pressed="selectedPeriod === period.key"
        >
          {{ period.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SubAccountsControlBar",
  props: {
    startDate: {
      type: String,
      default: ""
    },
    endDate: {
      type: String,
      default: ""
    },
    selectedAccountIds: {
      type: Array,
      default: () => []
    },
    availableAccounts: {
      type: Array,
      default: () => []
    },
    selectedPeriod: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isAccountsDropdownOpen: false,
      accountSearchQuery: "",
      accountSelectionWarning: "",
      showAccountSelectionWarning: false,
      warningTimeoutId: null,
      localStartDate: this.startDate,
      localEndDate: this.endDate,
      localSelectedAccountIds: [...this.selectedAccountIds],
      periodOptions: [
        { key: "last-month", label: "Last month", days: 30 },
        { key: "last-6-months", label: "Last 6 months", days: 180 },
        { key: "last-9-months", label: "Last 9 months", days: 270 }
      ]
    };
  },
  computed: {
    filteredAccounts() {
      if (!this.accountSearchQuery) {
        return this.availableAccounts;
      }
      const query = this.accountSearchQuery.toLowerCase();
      return this.availableAccounts.filter((account) => account.name.toLowerCase().includes(query));
    },
    selectedAccountsData() {
      return this.availableAccounts.filter((account) => this.localSelectedAccountIds.includes(account.id));
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
  },
  methods: {
    showAtLeastOneAccountWarning() {
      this.accountSelectionWarning = "At least one account must be selected.";
      this.showAccountSelectionWarning = true;
      if (this.warningTimeoutId) {
        clearTimeout(this.warningTimeoutId);
      }
      this.warningTimeoutId = setTimeout(() => {
        this.accountSelectionWarning = "";
        this.showAccountSelectionWarning = false;
        this.warningTimeoutId = null;
      }, 2500);
    },
    toggleAccountsDropdown() {
      this.isAccountsDropdownOpen = !this.isAccountsDropdownOpen;
    },
    handleClickOutside(event) {
      if (this.$refs.accountsDropdown && !this.$refs.accountsDropdown.contains(event.target)) {
        this.isAccountsDropdownOpen = false;
      }
    },
    isAccountSelected(accountId) {
      return this.localSelectedAccountIds.includes(accountId);
    },
    toggleAccount(account) {
      const index = this.localSelectedAccountIds.indexOf(account.id);
      if (index > -1) {
        if (this.localSelectedAccountIds.length === 1) {
          this.showAtLeastOneAccountWarning();
          return;
        }
        this.localSelectedAccountIds.splice(index, 1);
      } else {
        this.localSelectedAccountIds.push(account.id);
      }
      this.$emit('update:period', null);
      this.emitChange();
    },
    deselectAllAccounts() {
      this.localSelectedAccountIds = [];
      this.localSelectedAccountIds = [this.availableAccounts[0].id];
      this.$emit('update:period', null);
      this.emitChange();
    },
    selectAllAccounts() {
      this.localSelectedAccountIds = this.availableAccounts.map((account) => account.id);
      this.$emit('update:period', null);
      this.emitChange();
    },
    removeAccount(accountId) {
      const index = this.localSelectedAccountIds.indexOf(accountId);
      if (index > -1) {
        if (this.localSelectedAccountIds.length === 1) {
          this.showAtLeastOneAccountWarning();
          return;
        }
        this.localSelectedAccountIds.splice(index, 1);
      }
      this.$emit('update:period', null);
      this.emitChange();
    },
    selectPeriod(period) {
      // Emit period change to parent
      this.$emit('update:period', period.key);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - period.days);

      this.localStartDate = this.formatDate(startDate);
      this.localEndDate = this.formatDate(endDate);

      this.emitChange(true); // Pass flag to indicate period selection
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    onStartDateChange() {
      if (this.localStartDate && this.localEndDate && this.localStartDate > this.localEndDate) {
        this.localEndDate = this.localStartDate;
      }
      this.emitChange();
    },
    onEndDateChange() {
      if (this.localStartDate && this.localEndDate && this.localEndDate < this.localStartDate) {
        this.localEndDate = this.localStartDate;
      }
      this.emitChange();
    },
    emitChange(isPeriodSelection = false) {
      // Reset selectedPeriod only when dates are changed manually (not from period selection)
      if (!isPeriodSelection) {
        this.$emit('update:period', null);
      }

      this.$emit("change", {
        startDate: this.localStartDate,
        endDate: this.localEndDate,
        selectedAccountIds: [...this.localSelectedAccountIds]
      });
    }
  },
  watch: {
    startDate(newVal) {
      this.localStartDate = newVal;
    },
    endDate(newVal) {
      this.localEndDate = newVal;
    },
    selectedAccountIds(newVal) {
      this.localSelectedAccountIds = [...newVal];
    }
  }
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/_variables" as *;

$color-lime: #D9F99D;
$color-blue-light: #EBF2FF;
$color-blue-active-text: #0288D1;

.sub-accounts-control-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  border-bottom: 1px solid $color-gray-divider;
  padding: 12px 0;
  margin-bottom: 24px;

  .control-bar-content {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 24px;
    flex-wrap: wrap;
  }

  .accounts-section {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .date-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .vertical-separator {
    width: 1px;
    height: 32px;
    background-color: $color-gray-divider;
  }

  .section-label {
    font-size: 11px;
    font-weight: 600;
    color: $color-gray-text;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-right: 8px;
    white-space: nowrap;
  }
}

// Accounts Dropdown
.dropdown-container {
  position: relative;
  min-width: 200px;
  .dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 12px;
    border: 1px solid $color-gray-divider;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: $color-navy-medium;
    }

    &.is-open {
      border-color: $color-navy-medium;
      box-shadow: 0 0 0 2px rgba(2, 136, 209, 0.1);
    }

    .dropdown-label {
      font-size: 12px;
      font-weight: 600;
      color: $color-gray-text;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .dropdown-value {
      font-size: 14px;
      color: $color-navy-dark;
      font-weight: 500;
    }

    .dropdown-arrow {
      font-size: 12px;
      color: $color-gray-text;
      transition: transform 0.2s ease;}

    &.is-open .dropdown-arrow {
      transform: rotate(180deg);
    }
  }

  .dropdown-menu {
    display: block;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid $color-gray-divider;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 300px;
    overflow: hidden;
    margin-top: 4px;
    padding: 0;
    font-size: inherit;
    color: inherit;
    text-align: left;
    list-style: none;
    float: none;
    min-width: auto;
    .dropdown-header {
      padding: 12px;
      border-bottom: 1px solid $color-gray-divider;

      .search-input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid $color-gray-divider;
        border-radius: 4px;
        font-size: 14px;
        margin-bottom: 8px;

        &:focus {
          outline: none;
          border-color: $color-navy-medium;
          box-shadow: 0 0 0 2px rgba(2, 136, 209, 0.1);
        }
      }

      .quick-actions {
        display: flex;
        gap: 8px;

        .quick-action-btn {
          padding: 4px 12px;
          border: 1px solid $color-gray-divider;
          border-radius: 4px;
          background: white;
          font-size: 12px;
          font-weight: 500;
          color: $color-navy-dark;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: $color-gray-bg-light;
            border-color: $color-navy-medium;
          }
        }
      }
    }

    .accounts-list {
      max-height: 200px;
      overflow-y: auto;

      .account-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
          background: $color-gray-bg-light;
        }

        .account-checkbox {
          margin: 0;
        }

        .account-color-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .account-name {
          font-size: 14px;
          color: $color-navy-dark;
          font-weight: 500;
        }
      }

      .no-results {
        padding: 20px;
        text-align: center;
        color: $color-gray-text;
        font-size: 14px;
        font-style: italic;
      }
    }
  }
}

// Selected Chips Inline
.selected-chips-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;

  .account-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    background: $color-gray-bg-light;
    border: 1px solid $color-gray-divider;
    border-radius: 16px;
    font-size: 11px;

    .chip-color-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .chip-text {
      color: $color-navy-dark;
      font-weight: 500;
    }

    .chip-remove {
      background: none;
      border: none;
      color: $color-gray-text;
      cursor: pointer;
      padding: 1px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;

      &:hover {
        background: $color-gray-divider;
        color: $color-navy-dark;
      }

      i {
        font-size: 8px;
      }
    }
  }
}

// Date Pickers Inline
.date-pickers-inline {
  display: flex;
  align-items: center;
  gap: 8px;

  .date-input {
    padding: 6px 10px;
    border: 1px solid $color-gray-divider;
    border-radius: 4px;
    font-size: 13px;
    font-family: $font-family-portal;
    color: $color-navy-dark;
    min-width: 120px;

    &:focus {
      outline: none;
      border-color: $color-navy-medium;
      box-shadow: 0 0 0 2px rgba(2, 136, 209, 0.1);
    }
  }

  .date-separator {
    color: $color-gray-text;
    font-size: 14px;
    font-weight: 500;
    user-select: none;
  }
}

// Period Buttons
.period-section {
  display: flex;
  align-items: center;
  gap: 6px;

  .period-btn {
    padding: 8px 16px;
    border: 1px solid #E2E8F0;
    border-radius: 4px;
    background: white;
    color: $color-gray-text-dark;
    font-family: $font-family-portal;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: $color-gray-bg-light;
      border-color: $color-gray-text;
    }

    &.is-active {
      background: $color-blue-light;
      border-color: $color-blue-light;
      color: $color-navy-dark;
      font-weight: 600;

      &:hover {
        background: darken($color-blue-light, 8%);
        border-color: darken($color-blue-light, 8%);
      }
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .sub-accounts-control-bar {
    padding: 10px 0;
    margin-bottom: 16px;

    .control-bar-content {
      gap: 12px;
      justify-content: flex-start;
    }

    .accounts-section {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .date-section {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      .date-pickers-inline {
        flex-direction: column;
        gap: 6px;

        .date-separator {
          display: none;
        }
      }
    }

    .period-section {
      flex-direction: column;
      gap: 6px;
      width: 100%;

      .period-btn {
        width: 100%;
      }
    }

    .vertical-separator {
      display: none;
    }
  }
}

@media (min-width: 769px) and (max-width: 1023px) {
  .sub-accounts-control-bar {
    .control-bar-content {
      gap: 20px;
    }

    .date-pickers-inline {
      gap: 6px;

      .date-input {
        min-width: 110px;
        font-size: 12px;
      }
    }
  }
}
</style>
